import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseQueryResult } from '@tanstack/react-query';

import { ApiError } from '@/shared/api/http';
import { Alert } from '@/shared/ui/Alert';

import { useDatasetScore, useDatasetTrend, useScoreFactors } from '../api/scoring';
import type { Dimension, Score, ScoreFactors, Trend } from '../api/scoring';
import { defaultView } from '../model/factors';
import type { FactorFilter } from '../model/factors';
import { ScoreCalculation } from './ScoreCalculation';
import { ScoreFactorList } from './ScoreFactorList';
import { ScoreTrendChart } from './ScoreTrendChart';
import { ScoreVerdictCard } from './ScoreVerdictCard';

/* One dataset's score, as the Score tab: why the number is what it is and what to fix first, how it
 * is calculated, then how it has moved.
 *
 * **Two reads, and the second only explains the first.** The score is what the tab is about; its
 * factors (`GET /scores/{id}/factors`) say what raised and lowered it. So the score's own figure shows
 * as soon as it arrives, and a failure to read the factors is reported as exactly that — the score is
 * fine, its explanation is missing — rather than as a broken tab.
 *
 * **The filter is shared by three surfaces**, which is why it lives here: selecting a dimension in the
 * verdict's bar or in the calculation narrows the list of checks between them. It resets with the
 * score it belongs to, because a filter chosen for one run's checks means nothing for another's.
 */

const NOT_FOUND = 404;
/* The same window the score tile reads, so the two share one cached request. */
const TREND_DAYS = 30;

export interface DatasetScorePanelProps {
  readonly datasetId: string;
}

export function DatasetScorePanel({ datasetId }: DatasetScorePanelProps): ReactElement {
  const { t } = useTranslation();
  const score = useDatasetScore(datasetId);
  const factors = useScoreFactors(score.isSuccess ? score.data.id : undefined);
  const trend = useDatasetTrend(score.isSuccess ? datasetId : '', TREND_DAYS);

  if (score.isPending) {
    return (
      <div
        className="lq-dsp__loading"
        role="status"
        aria-label={t('dataset.score.loading')}
      >
        <span className="lq-skeleton__bar lq-dsp__skeleton" />
      </div>
    );
  }

  if (score.isError) {
    const notScored = score.error instanceof ApiError && score.error.status === NOT_FOUND;
    return notScored ? (
      <p className="lq-dsp__missing">{t('dataset.score.missing')}</p>
    ) : (
      <Alert tone="error" title={t('dataset.score.unreadableTitle')}>
        {score.error.message}
      </Alert>
    );
  }

  return (
    <div className="lq-sf">
      <Explanation
        key={score.data.id}
        score={score.data}
        factors={factors}
        datasetId={datasetId}
      />
      <TrendCard trend={trend} />
    </div>
  );
}

function Explanation({
  score,
  factors,
  datasetId,
}: {
  readonly score: Score;
  readonly factors: UseQueryResult<ScoreFactors>;
  readonly datasetId: string;
}): ReactElement {
  /* `null` until somebody chooses, so the default view follows the data: "Hurting" when anything
   * lost points, "Helping" when nothing did — an empty first view would read as a broken list. */
  const [chosen, setChosen] = useState<FactorFilter | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  /* Read once, so "Computed 3 hours ago" does not tick while somebody is reading the tab. */
  const [now] = useState(() => new Date());

  if (factors.isPending) {
    return (
      <>
        <ScoreVerdictCard score={score} explanation={{ state: 'loading' }} now={now} />
        <span className="lq-skeleton__bar lq-sf__listSkeleton" aria-hidden="true" />
      </>
    );
  }

  if (factors.isError) {
    return (
      <ScoreVerdictCard
        score={score}
        now={now}
        explanation={{
          state: 'error',
          retry: () => {
            void factors.refetch();
          },
        }}
      />
    );
  }

  const data = factors.data;
  const filter: FactorFilter = chosen ?? {
    view: defaultView(data.rules),
    dimension: null,
    query: '',
  };
  /* Selecting a dimension shows all of its checks, helping and hurting alike; selecting it again
   * clears it and leaves the view where it was. */
  const select = (dimension: Dimension): void => {
    setChosen(
      filter.dimension === dimension
        ? { ...filter, dimension: null }
        : { ...filter, view: 'all', dimension },
    );
    setShowAll(false);
  };

  return (
    <>
      <ScoreVerdictCard
        score={score}
        now={now}
        explanation={{
          state: 'ready',
          factors: data,
          selected: filter.dimension,
          onSelect: select,
        }}
      />
      {data.rules.length > 0 ? (
        <ScoreFactorList
          factors={data}
          datasetId={datasetId}
          filter={filter}
          onFilter={setChosen}
          showAll={showAll}
          onShowAll={setShowAll}
          open={open}
          onOpen={setOpen}
        />
      ) : null}
      <ScoreCalculation
        factors={data}
        executionId={score.execution_id}
        selected={filter.dimension}
        onSelect={select}
      />
    </>
  );
}

function TrendCard({ trend }: { readonly trend: UseQueryResult<Trend> }): ReactElement {
  const { t } = useTranslation();
  return (
    <article className="lq-sf__card lq-sf__trendCard">
      <h2 className="lq-sf__cardTitle lq-sf__trendTitle">
        {t('dataset.score.overTime')}
      </h2>
      {trend.isSuccess ? (
        <>
          {trend.data.points.filter((point) => point.value !== null).length >= 2 ? (
            <ScoreTrendChart trend={trend.data} />
          ) : (
            <p className="lq-dsp__note">{t('dataset.score.noTrend')}</p>
          )}
          {trend.data.crosses_a_model_change ? (
            <p className="lq-dsp__modelNote">{t('dataset.score.modelChangeNote')}</p>
          ) : null}
        </>
      ) : trend.isError ? (
        <p className="lq-dsp__note">{t('dataset.score.trendUnread')}</p>
      ) : (
        <span className="lq-skeleton__bar lq-dsp__chartSkeleton" aria-hidden="true" />
      )}
    </article>
  );
}
